import createPersistedReducer from '../../lib/createPersistedReducer.js';
import mapToCamelCase from '../../lib/mapToCamelCase.js';
import { sortByProps } from '../../lib/sortByProp.js';
import mergeByProp from 'lib/mergeByProp.js';

const initialState = {
  lastSettingsLoad: null,
  contact_detail_types: [],
  event_statuses: [],
  permissions: [],
  pipeline_lead_statuses: [],
  platforms: [],
  serviceProviders: [],
  todo_statuses: [],
  todo_types: [],
  utm_mediums: [],
  globalSummary: {},
  pipelineTemplates: [],
  leadSearch: [],
  redirectPath: null,
  event: {},
};

const persistConfig = {
  key: 'appReducer',
  blacklist: ['leadSearch', 'searchData', 'error', 'event', 'service_providers', 'refreshComponent', 'redirectPath'],
};

export default createPersistedReducer(
  initialState,
  {
    RESET_TO_INITIAL_STATE: (state) => {
      const copiedInitialState = { ...initialState };
      delete copiedInitialState.serviceProviders;
      delete copiedInitialState.globalSummary;
      return {
        ...state,
        ...copiedInitialState,
      };
    },

    SET_REDIRECT_FROM_LOGIN_PATH: (state, payload) => ({
      ...state,
      redirectPath: payload,
    }),

    DELETE_LAST_SETTINGS_LOAD: (state, payload) => ({
      ...state,
      lastSettingsLoad: null,
    }),

    GET_PIPELINE_TEMPLATES_SUCCESS: (state, payload) => ({
      ...state,
      pipelineTemplates: payload.data,
    }),

    GET_APP_SETTINGS_SUCCESS: (state, payload) => ({
      ...state,
      ...payload.data,
      lastSettingsLoad: new Date(),
      permissions: ((payload.data || {}).permissions || []).map(mapToCamelCase),
      serviceProviders: ((payload.data || {}).service_providers || []).map(mapToCamelCase),
    }),

    ACKNOWLEDGE_EVENT_TYPE: (state, { type, ids, serviceProviderId }) => {
      const useIds = ids && Array.isArray(ids);
      const spId = serviceProviderId || 0;
      // Acknowledge events based on ids array passed into
      // payload, or when there is no ids payload, we just acknowledge
      // all events of that type.
      let modifiedSet = (((state.event || {})[spId] || {})[type] || []).map((n = {}) => {
        const typeCheck = n.todo ? n.todo.event_data.todo : n.event_data;
        let acknowledged = useIds ? (ids.includes((typeCheck || {}).id) ? true : n.acknowledged) : true;
        return { ...n, acknowledged };
      });

      return {
        ...state,
        event: {
          ...state.event,
          [spId]: {
            ...(state.event[spId] || {}),
            [type]: modifiedSet,
          },
        },
      };
    },

    GET_EVENT: (state, payload) => {
      const event = payload.event;
      let obj = event.data ? event.data : event;
      if (obj.type === 'updated_todo' && !obj.event_data && obj.todo) {
        obj.event_data = { ...obj.todo.event_data.todo };
        obj.data = { event_data: { ...obj.todo.event_data.todo } };
      }
      const serviceProviderId = (obj || {}).service_provider_id ? (obj || {}).service_provider_id : payload.serviceProviderId;

      // Remove duplicates and clean old records.
      const maxEvents = 100;
      let existingSet = [...((state.event[serviceProviderId] || {})[obj.type] || [])].filter((n) => n.acknowledged !== true);

      if (existingSet.length > maxEvents) {
        existingSet = existingSet.slice(existingSet.length - maxEvents);
      }
      const match = existingSet.find((n) => ((n || {}).event_data || {}).id === ((obj.data || {}).event_data || {}).id);

      if (!match) {
        existingSet.push(obj);
      }

      return {
        ...state,
        event: {
          ...(state.event || {}),
          [serviceProviderId]: {
            ...((state.event || {})[serviceProviderId] || {}),
            [obj.type]: existingSet,
          },
        },
      };
    },

    GET_SERVICE_PROVIDERS_SUCCESS: (state, payload) => ({
      ...state,
      serviceProviders: [...payload.data].map(mapToCamelCase).map((n) => ({
        id: n.id,
        name: n.providerName,
        active: n.active,
        incomingUnreadMessageCount: n.incomingUnreadMessageCount,
      })),
    }),

    PUT_SERVICE_PROVIDER_SUCCESS: (state, payload) => ({
      ...state,
      serviceProviders: state.serviceProviders.map((n) =>
        payload.data.id !== n.id
          ? n
          : {
              ...n,
              active: payload.data.active,
            }
      ),
    }),

    GET_LEAD_SEARCH_SUCCESS: (state, payload) => {
      const { leads } = payload.data;
      let payloadOrdered = (leads || []).map((n) => {
        return {
          ...n,
          contacts: String(n.contacts_string).split(','),
        };
      });
      payloadOrdered = payloadOrdered.sort(sortByProps(['score', 'first_name', 'last_name'], ['desc', 'asc', 'asc']));
      return {
        ...state,
        leadSearch: payloadOrdered,
      };
    },

    DELETE_LEAD_SEARCH: (state, payload) => ({
      ...state,
      leadSearch: [],
    }),

    GET_GLOBAL_LEADLOOP_SUMMARY_SUCCESS: (state, payload) => ({
      ...state,
      globalSummary: {
        ...state.globalSummary,
        [payload.data.days]: {
          ...mapToCamelCase(payload.data),
          date: new Date(),
        },
      },
    }),
  },
  persistConfig
);
